import { clone } from '@jack-henry/frontend-utils/functions';
import { CompanyAccountModelDto, StpModelDto } from '@treasury/api/channel';
import { CompanyAccount, emptyCompanyAccount } from '../company/company-account.entity';

const nullStopPayment: StpModelDto = {
    id: 0,
    account: {
        ...emptyCompanyAccount,
    },
    checkNumbers: [],
    checkDate: '',
    checkAmount: 0,
    payee: '',
    remarks: [''],
    createdDate: '',
    createdBy: '',
    enteredBy: '',
    expirationDate: '',
    sequenceNumber: '',
    status: '',
    permissions: [{ id: 0, updatedDate: '', permission: '' }],
    totalApprovalsRequired: 0,
    completedApprovals: [{ name: '', dateApproved: '' }],
    availableApprovers: [''],
    completedApprovalCount: 0,
};

export class StopPayment {
    constructor(protected dto = clone(nullStopPayment)) {}

    public get id() {
        return this.dto.id || 0;
    }

    public set id(id) {
        this.dto.id = id;
    }

    public get account() {
        return new CompanyAccount(this.dto.account || ({} as CompanyAccountModelDto));
    }

    public set account(account: CompanyAccount) {
        this.dto.account = account.toDto() as CompanyAccountModelDto;
    }

    public get checkNumbers() {
        return this.dto.checkNumbers || [];
    }

    public set checkNumbers(checkNumbers) {
        this.dto.checkNumbers = checkNumbers;
    }

    public get checkDate() {
        return this.dto.checkDate;
    }

    public set checkDate(checkDate) {
        this.dto.checkDate = checkDate;
    }

    public get checkAmount() {
        return this.dto.checkAmount || 0;
    }

    public set checkAmount(checkAmount) {
        this.dto.checkAmount = checkAmount;
    }

    public get payee() {
        return this.dto.payee || '';
    }

    public set payee(payee) {
        this.dto.payee = payee;
    }

    public get remarks() {
        return this.dto.remarks || [''];
    }

    public set remarks(remarks) {
        this.dto.remarks = remarks;
    }

    public get createdDate() {
        return this.dto.createdDate || '';
    }

    public set createdDate(createdDate) {
        this.dto.createdDate = createdDate;
    }

    public get createdBy() {
        return this.dto.createdBy || '';
    }

    public set createdBy(createdBy) {
        this.dto.createdBy = createdBy;
    }

    public get enteredBy() {
        return this.dto.enteredBy || '';
    }

    public set enteredBy(enteredBy) {
        this.dto.enteredBy = enteredBy;
    }

    public get expirationDate() {
        return this.dto.expirationDate || '';
    }

    public set expirationDate(expirationDate) {
        this.dto.expirationDate = expirationDate;
    }

    public get sequenceNumber() {
        return this.dto.sequenceNumber || '';
    }

    public set sequenceNumber(sequenceNumber) {
        this.dto.sequenceNumber = sequenceNumber;
    }

    public get status() {
        return this.dto.status || '';
    }

    public set status(status) {
        this.dto.status = status;
    }

    public get permissions() {
        return this.dto.permissions || [{ id: 0, updatedDate: '', permission: '' }];
    }

    public set permissions(permissions) {
        this.dto.permissions = permissions;
    }

    public get totalApprovalsRequired() {
        return this.dto.totalApprovalsRequired || 0;
    }

    public set totalApprovalsRequired(totalApprovalsRequired) {
        this.dto.totalApprovalsRequired = totalApprovalsRequired;
    }

    public get completedApprovals() {
        return this.dto.completedApprovals || [{ name: '', dateApproved: '' }];
    }

    public set completedApprovals(completedApprovals) {
        this.dto.completedApprovals = completedApprovals;
    }

    public get numberOfApprovalsCompleted() {
        return this.dto.completedApprovalCount || 0;
    }

    private _checkNumberType: 'range' | 'specific' = 'specific';

    public get checkNumberType(): 'range' | 'specific' {
        return this._checkNumberType;
    }

    public set checkNumberType(checkNumberType: 'range' | 'specific') {
        this._checkNumberType = checkNumberType;
    }

    public isValid() {
        const hasAccount = this.account && this.account.accountDisplayLabel;
        const hasCheckNumbers = this.checkNumbers && this.checkNumbers.length > 0;
        let hasValidCheckNumber = false;
        if (this.checkNumberType === 'range') {
            hasValidCheckNumber =
                this.checkNumbers.every(checkNumber => {
                    return checkNumber && checkNumber?.toString().length > 0;
                }) &&
                this.checkNumbers.length === 2 &&
                this.checkNumbers[0] < this.checkNumbers[1];
        } else {
            hasValidCheckNumber = this.checkNumbers[0]?.toString().length > 0;
        }
        return hasAccount && hasCheckNumbers && hasValidCheckNumber;
    }

    public toDto() {
        return this.dto;
    }
}
